import type {ReactElement} from 'react';
import React from 'react';
import {BaseTextListOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-text-list-option';
import type {LIST_STYLE, LIST_TYPES} from '@PosterWhiteboard/items/text-item/text-list';
import type {FillObject} from '@PosterWhiteboard/classes/fill.class';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyFromFillOfListOfActiveSlideItem} from '@Hooks/poster-editor/useGetKeyFromFillOfListOfActiveSlideItem';
import {useGetKeyFromListOfActiveSlideItem} from '@Hooks/poster-editor/useGetKeyFromListOfActiveSlideItem';

interface TextSlideListOptionProps {
  onClickColorFill(): void;
}

export function TextSlideListOption({...props}: TextSlideListOptionProps): ReactElement {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const fillColor = useGetKeyFromFillOfListOfActiveSlideItem('fillColor');
  const fillType = useGetKeyFromFillOfListOfActiveSlideItem('fillType');
  const listType = useGetKeyFromListOfActiveSlideItem('type');
  const listStyle = useGetKeyFromListOfActiveSlideItem('style');
  const listWidth = useGetKeyFromListOfActiveSlideItem('width');

  if (!activeItem?.isSlideshow()) {
    throw new Error(`Expected slideshow item but got ${activeItem?.gitype}`);
  }

  if (!activeSlide?.isTextSlide()) {
    throw new Error(`Expected text slideshow item but got ${activeSlide?.gitype}`);
  }

  if (!activeSlideUID) {
    throw new Error('unable to get the selected text slide');
  }

  if (!fillColor || fillType === undefined || listType === undefined || listStyle === undefined || listWidth === undefined) {
    throw new Error('unable to get the list properties for text slide');
  }

  const updateListObject = (type: LIST_TYPES, styleToApply?: LIST_STYLE): void => {
    let style: LIST_STYLE;
    if (!styleToApply) {
      style = activeSlide.list.getDefaultListStyleFromType();
    } else {
      style = styleToApply;
    }

    let newFillObject: Partial<FillObject> = {};

    if (!activeSlide.list.hasList()) {
      newFillObject = {
        fillColor: fillColor,
        fillType: fillType,
      };
    }

    void activeItem.updateSlideFromObject(activeSlideUID, {
      list: {
        type,
        style,
        fill: newFillObject,
      },
    });
  };

  return (
    <BaseTextListOption
      onClickColorObject={(): void => {
        props.onClickColorFill();
      }}
      listObject={{
        type: listType,
        fill: {
          fillType: fillType,
          fillColor: fillColor,
        },
        style: listStyle,
        width: listWidth,
      }}
      isOrdered={activeSlide.list.isOrdered()}
      isUnordered={activeSlide.list.isUnordered()}
      onUpdateListObject={updateListObject}
    />
  );
}
